import type { ContactActivity } from '../../typescript-types/contact/contact-activity';

import { ACTIVITY_TYPES } from '../../util/activity-types';

/**
 * Checks to make sure that a given contact activity has the nested object necessary for
 * us to render the details about that contact activity. It should be pretty rare for a contact
 * activity to be missing it's nested object, but it's possible if there was some kind of issue
 * adding the call, status, message, or what-have-you in the database. We just ignore these
 * orphaned contact activities.
 */
export const isContactActivityValid = (contactActivity: ContactActivity) => {
  const { message, call, status } = contactActivity;

  if (contactActivity.type === ACTIVITY_TYPES.CALL && !call) {
    return false;
  }
  if (contactActivity.type === ACTIVITY_TYPES.STATUS && !status) {
    return false;
  }
  if (contactActivity.type === ACTIVITY_TYPES.MESSAGE && !message) {
    return false;
  }

  // This filters out contact logs unless they are for things that aren't messages
  // This is mostly backwards compatible code, as you can't even log a call this way
  // in the product any more (and the call functionality in the product gets logged
  // as a type === 'call')
  return (
    contactActivity.type !== ACTIVITY_TYPES.CONTACT ||
    !contactActivity.contact ||
    !contactActivity.contact.messageId
  );
};

/**
 * This gets the name to display for the actor who took the action for this contact activity
 * NOTE: This current assumes all contact activities being displayed are for applicants. When
 *         or if we add an activity list that includes employees, we will need to alter this
 */
export const getAgentForActivity = (activity: ContactActivity) => {
  if (activity.type === ACTIVITY_TYPES.UPDATE) {
    return 'The applicant';
  }
  if (activity.isAutomated) {
    return 'Team Engine (automatically)';
  }
  if (activity.user !== null && activity.user !== undefined) {
    // If we take action on an applicant on behalf of a customer, don't show our name
    if (activity.user.isAdmin) {
      return 'Team Engine';
    }
    return `${activity.user.firstName} ${activity.user.lastName}`;
  }
  // If this is a status change without a user, we just say "Applicant" unless the type
  // is "status"
  if (activity.type !== ACTIVITY_TYPES.STATUS) {
    return 'The applicant';
  }

  return null;
};
